/**
 * Execute Section Tool Schema
 * 
 * Schema for the tool that executes a section build via ExecutionAgent.
 * 
 * Updated to use the new multi-slide schema with:
 * - Pre-specified areas per slide
 * - Global and per-slide context
 * - Reference slides for styling
 */

import { z } from "zod";
import { 
    ExecutionAgentParamsSchema, 
    ExecutionAgentResponseSchema,
    AreaSchema,
    ContextItemSchema,
    SlideToBuildSchema,
} from "../../executionAgent.schema";

// Re-export commonly used types
export { AreaSchema, ContextItemSchema, SlideToBuildSchema };
export type { Area, ContextItem, SlideToBuild } from "../../executionAgent.schema";

// ═══════════════════════════════════════════════════════════════════════════
// EXECUTE SECTION PARAMS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ExecuteSectionParams now uses the new ExecutionAgentParams schema.
 * 
 * Key fields:
 * - planId: Plan ID for this build session
 * - sectionId, sectionTitle: Section identification (optional)
 * - globalContext: Context that applies to all slides
 * - referenceSlideIndices: Style guide slides for visual reference
 * - slides: Array of SlideToBuild (sourceSlideIndex, position, areas, context)
 * - excelContext: Excel data for charts/tables
 */
export const ExecuteSectionParamsSchema = ExecutionAgentParamsSchema;

export type ExecuteSectionParams = z.infer<typeof ExecuteSectionParamsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// EXECUTE SECTION RESPONSE
// ═══════════════════════════════════════════════════════════════════════════

export const ExecuteSectionResponseSchema = z.object({
    success: z.boolean(),
    sectionId: z.string().optional(),
    sectionTitle: z.string().optional(),
    slidesBuilt: z.number(),
    areasBuilt: z.number(),
    checkpointBefore: z.string().describe("Section checkpoint before execution"),
    checkpointAfter: z.string().describe("Section checkpoint after execution"),
    errors: z.array(z.string()).optional(),
    executionResult: ExecutionAgentResponseSchema.optional(),
    rolledBack: z.boolean().optional().describe("True if section failed and was rolled back to checkpoint_before"),
});

export type ExecuteSectionResponse = z.infer<typeof ExecuteSectionResponseSchema>;
